import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../context/AuthContext';
import { useCurrentUserProfile } from '../hooks/useCurrentUserProfile';
import { hasAdminRole, hasSuperAdminRole } from '../utils/roles';
import { supabase } from '../services/supabase';
import {
  buildGroupAttireCategoryOptions,
  FALLBACK_GROUP_ATTIRE_CATEGORIES,
  normalizeGroupAttireCategoryName,
  resolveGroupAttireCategoryName,
  sanitizeGroupAttireCategoryName,
} from '../utils/groupAttireCategories';

const Page = styled.div`
  background: var(--background);
  padding: 1.5rem;
  min-height: 100%;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1.25rem;
  flex-wrap: wrap;
`;

const HeaderTitle = styled.h1`
  margin: 0;
  font-size: 1.6rem;
  color: var(--onSurface);
`;

const HeaderMeta = styled.p`
  margin: 0.35rem 0 0;
  font-size: 0.9rem;
  color: var(--onSurfaceVariant);
`;

const Card = styled.section`
  background: var(--surface);
  border: 1px solid var(--outline);
  border-radius: 14px;
  box-shadow: 0 12px 24px rgba(17, 24, 39, 0.06);
  padding: 1rem;
  margin-bottom: 1rem;
`;

const SectionTitle = styled.h2`
  margin: 0 0 0.75rem;
  color: var(--onSurface);
  font-size: 1rem;
`;

const SearchRow = styled.div`
  display: flex;
  gap: 0.6rem;
  align-items: stretch;
  flex-wrap: wrap;
`;

const Input = styled.input`
  padding: 0.65rem 0.75rem;
  border-radius: 10px;
  border: 1px solid var(--outline);
  font-size: 0.95rem;
  color: var(--onSurface);
  background: var(--surfaceVariant);
  min-height: 42px;

  &:focus {
    outline: none;
    border-color: #93c5fd;
    background: var(--surface);
  }
`;

const SearchInput = styled(Input)`
  flex: 1;
  min-width: 220px;
`;

const Select = styled.select`
  padding: 0.65rem 0.75rem;
  border-radius: 10px;
  border: 1px solid var(--outline);
  font-size: 0.95rem;
  color: var(--onSurface);
  background: var(--surfaceVariant);
  min-height: 42px;

  &:focus {
    outline: none;
    border-color: #93c5fd;
    background: var(--surface);
  }
`;

const Button = styled.button`
  border: 1px solid #cbd5f5;
  border-radius: 999px;
  padding: 0.55rem 1.1rem;
  font-weight: 600;
  color: var(--onSurface);
  background: var(--surfaceVariant);
  cursor: pointer;

  &:disabled {
    opacity: 0.65;
    cursor: not-allowed;
  }
`;

const PrimaryButton = styled.button`
  border: none;
  border-radius: 999px;
  padding: 0.6rem 1.25rem;
  font-weight: 600;
  color: var(--surface);
  background: #1d4ed8;
  cursor: pointer;

  &:disabled {
    background: var(--onSurfaceVariant);
    cursor: not-allowed;
  }
`;

const SecondaryButton = styled.button`
  border: 1px solid #cbd5f5;
  border-radius: 999px;
  padding: 0.6rem 1.25rem;
  font-weight: 600;
  color: var(--onSurface);
  background: var(--surfaceVariant);
  cursor: pointer;
`;

const TinyButton = styled.button`
  border: 1px solid var(--outline);
  border-radius: 999px;
  background: var(--surface);
  color: var(--onSurface);
  font-size: 0.75rem;
  font-weight: 600;
  padding: 0.3rem 0.65rem;
  cursor: pointer;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const DangerButton = styled(Button)`
  border-color: #fecaca;
  color: #991b1b;
  background: #fff1f2;
`;

const FormGrid = styled.div`
  display: grid;
  gap: 1rem;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
`;

const FormField = styled.label`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  font-size: 0.85rem;
  color: var(--onSurfaceVariant);
`;

const ToggleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 0.9rem;
  color: #1f2937;
`;

const InlineActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-top: 1rem;
`;

const MemberList = styled.div`
  display: grid;
  gap: 0.55rem;
`;

const MemberItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.65rem 0.75rem;
  border: 1px solid var(--outline);
  border-radius: 10px;
  background: var(--surface);
`;

const MemberMain = styled.div`
  display: grid;
  gap: 0.1rem;
`;

const MemberActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.45rem;
  flex-wrap: wrap;
`;

const RoleBadge = styled.span<{ $role: string }>`
  display: inline-block;
  font-size: 0.75rem;
  font-weight: 600;
  border-radius: 999px;
  padding: 0.15rem 0.5rem;
  color: ${({ $role }) => ($role === 'admin' ? '#1e3a8a' : 'var(--onSurfaceVariant)')};
  background: ${({ $role }) => ($role === 'admin' ? '#dbeafe' : 'var(--surfaceVariant)')};
`;

const HelperText = styled.p<{ $tone?: 'error' | 'success' }>`
  margin: 0.6rem 0 0;
  font-size: 0.85rem;
  color: ${({ $tone }) => ($tone === 'error' ? 'var(--error)' : '#047857')};
`;

const MutedText = styled.p`
  margin: 0;
  color: var(--onSurfaceVariant);
  font-size: 0.88rem;
`;

const MetaLine = styled.span`
  color: var(--onSurfaceVariant);
  font-size: 0.82rem;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1rem;
`;

const List = styled.div`
  display: grid;
  gap: 0.55rem;
  max-height: 380px;
  overflow: auto;
`;

const ListItem = styled.div`
  border: 1px solid var(--outline);
  border-radius: 10px;
  padding: 0.65rem 0.75rem;
  background: var(--surfaceVariant);
  display: grid;
  gap: 0.25rem;
`;

const PaginationRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.5rem;
  margin-top: 0.65rem;
`;

const PaginationInfo = styled.span`
  color: var(--onSurfaceVariant);
  font-size: 0.82rem;
  min-width: 4.5rem;
  text-align: center;
`;

const BackLink = styled(Link)`
  text-decoration: none;
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
`;

const ActionButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

type GroupMemberRole = 'admin' | 'moderator' | 'member';

type GroupMemberRow = {
  id: string;
  group_id: string;
  user_id: string;
  role: GroupMemberRole;
  joined_at?: string | null;
  user?: {
    id?: string;
    full_name?: string | null;
    email?: string | null;
  } | null;
};

type SearchUserRow = {
  id: string;
  full_name?: string | null;
  email?: string | null;
  phone?: string | null;
};

type GroupPostRow = {
  id: string;
  content?: string | null;
  author_name?: string | null;
  type?: string | null;
  status?: string | null;
  created_at?: string | null;
};

type GroupMediaRow = {
  id: string;
  type?: string | null;
  filename?: string | null;
  uploaded_by_name?: string | null;
  uploaded_at?: string | null;
  size?: number | null;
  url?: string | null;
  storage_bucket?: string | null;
  storage_path?: string | null;
};

type GroupChatRow = {
  id: string;
  name?: string | null;
  description?: string | null;
  message_count?: number | null;
  updated_at?: string | null;
  is_active?: boolean | null;
};

type GroupJoinRequestRow = {
  id: string;
  group_id: string;
  invited_user_id: string;
  invited_by?: string | null;
  status?: string | null;
  message?: string | null;
  created_at?: string | null;
  expires_at?: string | null;
  requesting_user?: {
    id?: string;
    full_name?: string | null;
    email?: string | null;
    phone?: string | null;
  } | null;
};

type GroupAttireLinkRow = {
  id: string;
  group_id: string;
  title: string;
  url: string;
  image_url?: string | null;
  category: string;
  sort_order?: number | null;
  created_at?: string | null;
  updated_at?: string | null;
};

type GroupAttireCategoryRow = {
  id: string;
  group_id: string;
  name: string;
  created_at?: string | null;
  updated_at?: string | null;
};

type GroupAttireDefaultCategoryRow = {
  id: string;
  name: string;
  sort_order?: number | null;
  created_at?: string | null;
  updated_at?: string | null;
};

const formatDate = (value?: string | null) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
};

const formatFileSize = (bytes?: number | null) => {
  if (!bytes || bytes <= 0) return '-';
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(1)} KB`;
  const mb = kb / 1024;
  return `${mb.toFixed(1)} MB`;
};

const parseAttireEnabled = (settings: unknown) => {
  if (!settings || typeof settings !== 'object') return false;
  return (settings as { attireEnabled?: unknown }).attireEnabled === true;
};

const GROUP_CATEGORY_OPTIONS = [
  { value: 'life-stage', label: 'Life Stage' },
  { value: 'family', label: 'Family' },
  { value: 'seniors', label: 'Seniors' },
  { value: 'youth', label: 'Youth' },
  { value: 'ministry', label: 'Ministry' },
  { value: 'bible-study', label: 'Bible Study' },
  { value: 'prayer', label: 'Prayer' },
  { value: 'outreach', label: 'Outreach' },
  { value: 'other', label: 'Other' },
] as const;

const normalizeExternalUrl = (value: string) => {
  const trimmed = value.trim();
  if (!trimmed) return trimmed;
  return /^[a-z]+:/i.test(trimmed) ? trimmed : `https://${trimmed}`;
};

const MAX_ATTIRE_IMAGE_SIZE_BYTES = 5 * 1024 * 1024;

const uploadAttireImage = async (file: File, groupId: string) => {
  if (!file.type.startsWith('image/')) {
    throw new Error('Please select an image file.');
  }
  if (file.size > MAX_ATTIRE_IMAGE_SIZE_BYTES) {
    throw new Error('Please upload an image smaller than 5MB.');
  }

  const rawExtension = file.name.split('.').pop() || 'jpg';
  const extension = rawExtension.toLowerCase().replace(/[^a-z0-9]/g, '') || 'jpg';
  const fileName = `group-attire-links/${groupId}/${Date.now()}_${Math.random().toString(36).slice(2)}.${extension}`;
  const { error: uploadError } = await supabase.storage.from('post-images').upload(fileName, file, {
    upsert: false,
    contentType: file.type || 'image/jpeg',
  });
  if (uploadError) throw uploadError;

  const { data } = supabase.storage.from('post-images').getPublicUrl(fileName);
  return data.publicUrl;
};

const isStorageObjectMissingError = (error: unknown) => {
  if (!error || typeof error !== 'object') return false;
  const maybeMessage = (error as { message?: unknown }).message;
  return typeof maybeMessage === 'string' && maybeMessage.toLowerCase().includes('object not found');
};

const STORAGE_URI_PREFIX = 'storage://';
const GROUP_MEDIA_PRIVATE_BUCKET = 'group-media-assets';
const GROUP_MEDIA_SIGNED_URL_TTL_SECONDS = 3600;

const getGroupMediaStorageReference = ({
  url,
  storageBucket,
  storagePath,
}: {
  url?: string | null;
  storageBucket?: string | null;
  storagePath?: string | null;
}) => {
  if (storageBucket && storagePath) {
    return { bucket: storageBucket, path: storagePath };
  }

  if (!url) return null;

  if (url.startsWith(STORAGE_URI_PREFIX)) {
    const rawPath = url.slice(STORAGE_URI_PREFIX.length);
    const slashIndex = rawPath.indexOf('/');
    if (slashIndex <= 0) return null;
    return {
      bucket: rawPath.slice(0, slashIndex),
      path: rawPath.slice(slashIndex + 1),
    };
  }

  try {
    const parsed = new URL(url);
    const markers = [
      '/storage/v1/object/public/',
      '/storage/v1/object/sign/',
      '/storage/v1/object/authenticated/',
    ];

    for (const marker of markers) {
      const index = parsed.pathname.indexOf(marker);
      if (index === -1) continue;
      const remainder = parsed.pathname.slice(index + marker.length);
      const slashIndex = remainder.indexOf('/');
      if (slashIndex <= 0) return null;
      return {
        bucket: remainder.slice(0, slashIndex),
        path: decodeURIComponent(remainder.slice(slashIndex + 1)),
      };
    }
  } catch {
    return null;
  }

  return null;
};

const resolveGroupMediaUrl = async ({
  url,
  storageBucket,
  storagePath,
}: {
  url?: string | null;
  storageBucket?: string | null;
  storagePath?: string | null;
}) => {
  const ref = getGroupMediaStorageReference({ url, storageBucket, storagePath });
  if (!ref) return url ?? null;

  if (ref.bucket === GROUP_MEDIA_PRIVATE_BUCKET) {
    const { data, error } = await supabase.storage
      .from(ref.bucket)
      .createSignedUrl(ref.path, GROUP_MEDIA_SIGNED_URL_TTL_SECONDS);
    if (error) {
      if (isStorageObjectMissingError(error)) {
        console.warn('Group media asset missing from storage', {
          bucket: ref.bucket,
          path: ref.path,
        });
        return null;
      }
      throw error;
    }
    return data?.signedUrl ?? url ?? null;
  }

  const { data } = supabase.storage.from(ref.bucket).getPublicUrl(ref.path);
  return data.publicUrl || url || null;
};

const GroupManagePage: React.FC = () => {
  const MEMBERS_PER_PAGE = 10;
  const POSTS_PER_PAGE = 6;
  const MEDIA_PER_PAGE = 6;
  const CHATS_PER_PAGE = 6;

  const { groupId } = useParams<{ groupId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { data: profile } = useCurrentUserProfile(user?.id);
  const roles = profile?.roles ?? user?.user_metadata?.roles;
  const canManageGroups = hasAdminRole(roles);
  const canToggleAttire = hasSuperAdminRole(roles);

  const [groupName, setGroupName] = useState<string>('');
  const [groupCategory, setGroupCategory] = useState('other');
  const [groupMaxMembers, setGroupMaxMembers] = useState('');
  const [groupCurrentMembers, setGroupCurrentMembers] = useState(0);
  const [settingsName, setSettingsName] = useState('');
  const [settingsCategory, setSettingsCategory] = useState('other');
  const [settingsMaxMembers, setSettingsMaxMembers] = useState('');
  const [isSavingGroupSettings, setIsSavingGroupSettings] = useState(false);
  const [groupSettingsError, setGroupSettingsError] = useState<string | null>(null);
  const [groupSettingsSuccess, setGroupSettingsSuccess] = useState<string | null>(null);
  const [currentGroupRole, setCurrentGroupRole] = useState<GroupMemberRole | null>(null);
  const [isResolvingGroupAccess, setIsResolvingGroupAccess] = useState(false);
  const [groupMembers, setGroupMembers] = useState<GroupMemberRow[]>([]);
  const [isMembersLoading, setIsMembersLoading] = useState(false);
  const [manageError, setManageError] = useState<string | null>(null);
  const [manageSuccess, setManageSuccess] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SearchUserRow[]>([]);
  const [isSearchingUsers, setIsSearchingUsers] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [busyActionKey, setBusyActionKey] = useState<string | null>(null);
  const [isDeletingGroup, setIsDeletingGroup] = useState(false);
  const [deleteConfirmationText, setDeleteConfirmationText] = useState('');
  const [isLoadingDetails, setIsLoadingDetails] = useState(true);
  const [groupDetailsError, setGroupDetailsError] = useState<string | null>(null);
  const [attireEnabled, setAttireEnabled] = useState(false);
  const [attireLinks, setAttireLinks] = useState<GroupAttireLinkRow[]>([]);
  const [attireDefaultCategories, setAttireDefaultCategories] = useState<GroupAttireDefaultCategoryRow[]>([]);
  const [attireCustomCategories, setAttireCustomCategories] = useState<GroupAttireCategoryRow[]>([]);
  const [isAttireLoading, setIsAttireLoading] = useState(false);
  const [isAttireCategoriesLoading, setIsAttireCategoriesLoading] = useState(false);
  const [attireError, setAttireError] = useState<string | null>(null);
  const [attireSuccess, setAttireSuccess] = useState<string | null>(null);
  const [attireBusyKey, setAttireBusyKey] = useState<string | null>(null);
  const [editingAttireId, setEditingAttireId] = useState<string | null>(null);
  const [isUploadingAttireImage, setIsUploadingAttireImage] = useState(false);
  const [newDefaultAttireCategoryName, setNewDefaultAttireCategoryName] = useState('');
  const [newAttireCategoryName, setNewAttireCategoryName] = useState('');
  const [attireForm, setAttireForm] = useState<{
    title: string;
    url: string;
    imageUrl: string;
    category: string;
  }>({
    title: '',
    url: '',
    imageUrl: '',
    category: FALLBACK_GROUP_ATTIRE_CATEGORIES[0] ?? '',
  });

  const [posts, setPosts] = useState<GroupPostRow[]>([]);
  const [postsError, setPostsError] = useState<string | null>(null);
  const [isPostsLoading, setIsPostsLoading] = useState(false);

  const [media, setMedia] = useState<GroupMediaRow[]>([]);
  const [mediaError, setMediaError] = useState<string | null>(null);
  const [isMediaLoading, setIsMediaLoading] = useState(false);

  const [chats, setChats] = useState<GroupChatRow[]>([]);
  const [chatsError, setChatsError] = useState<string | null>(null);
  const [isChatsLoading, setIsChatsLoading] = useState(false);
  const [joinRequests, setJoinRequests] = useState<GroupJoinRequestRow[]>([]);
  const [pendingInvitations, setPendingInvitations] = useState<GroupJoinRequestRow[]>([]);
  const [joinRequestsError, setJoinRequestsError] = useState<string | null>(null);
  const [isJoinRequestsLoading, setIsJoinRequestsLoading] = useState(false);
  const [membersPage, setMembersPage] = useState(1);
  const [postsPage, setPostsPage] = useState(1);
  const [mediaPage, setMediaPage] = useState(1);
  const [chatsPage, setChatsPage] = useState(1);
  const confirmationTarget = groupName || groupId;
  const isGroupAdmin = currentGroupRole === 'admin';
  const canAccessGroupManagement = canManageGroups || isGroupAdmin;
  const canEditAttireContent = canManageGroups || isGroupAdmin;

  const adminCount = useMemo(
    () => groupMembers.filter((member) => member.role === 'admin').length,
    [groupMembers]
  );

  const orderedMembers = useMemo(() => {
    const rows = [...groupMembers];
    rows.sort((left, right) => {
      if (left.role === 'admin' && right.role !== 'admin') return -1;
      if (left.role !== 'admin' && right.role === 'admin') return 1;
      const leftName = left.user?.full_name?.trim().toLowerCase() ?? '';
      const rightName = right.user?.full_name?.trim().toLowerCase() ?? '';
      return leftName.localeCompare(rightName);
    });
    return rows;
  }, [groupMembers]);

  const membersTotalPages = Math.max(1, Math.ceil(orderedMembers.length / MEMBERS_PER_PAGE));
  const postsTotalPages = Math.max(1, Math.ceil(posts.length / POSTS_PER_PAGE));
  const mediaTotalPages = Math.max(1, Math.ceil(media.length / MEDIA_PER_PAGE));
  const chatsTotalPages = Math.max(1, Math.ceil(chats.length / CHATS_PER_PAGE));

  const pagedMembers = useMemo(() => {
    const start = (membersPage - 1) * MEMBERS_PER_PAGE;
    return orderedMembers.slice(start, start + MEMBERS_PER_PAGE);
  }, [orderedMembers, membersPage, MEMBERS_PER_PAGE]);

  const pagedPosts = useMemo(() => {
    const start = (postsPage - 1) * POSTS_PER_PAGE;
    return posts.slice(start, start + POSTS_PER_PAGE);
  }, [posts, postsPage, POSTS_PER_PAGE]);

  const pagedMedia = useMemo(() => {
    const start = (mediaPage - 1) * MEDIA_PER_PAGE;
    return media.slice(start, start + MEDIA_PER_PAGE);
  }, [media, mediaPage, MEDIA_PER_PAGE]);

  const pagedChats = useMemo(() => {
    const start = (chatsPage - 1) * CHATS_PER_PAGE;
    return chats.slice(start, start + CHATS_PER_PAGE);
  }, [chats, chatsPage, CHATS_PER_PAGE]);

  const orderedAttireLinks = useMemo(() => {
    return [...attireLinks].sort((left, right) => {
      const leftOrder = left.sort_order ?? Number.MAX_SAFE_INTEGER;
      const rightOrder = right.sort_order ?? Number.MAX_SAFE_INTEGER;
      if (leftOrder !== rightOrder) return leftOrder - rightOrder;
      return (left.created_at ?? '').localeCompare(right.created_at ?? '');
    });
  }, [attireLinks]);

  const attireCategoryOptions = useMemo(
    () =>
      buildGroupAttireCategoryOptions(
        attireDefaultCategories.map((category) => category.name),
        attireCustomCategories.map((category) => category.name),
        attireLinks.map((link) => link.category)
      ),
    [attireCustomCategories, attireDefaultCategories, attireLinks]
  );

  const resetAttireForm = useCallback(() => {
    setEditingAttireId(null);
    setAttireForm({
      title: '',
      url: '',
      imageUrl: '',
      category: attireCategoryOptions[0] ?? FALLBACK_GROUP_ATTIRE_CATEGORIES[0] ?? '',
    });
    setNewAttireCategoryName('');
  }, [attireCategoryOptions]);

  const loadAttireLinks = useCallback(async () => {
    if (!groupId) return;
    setIsAttireLoading(true);
    setAttireError(null);

    const { data, error } = await supabase
      .from('group_attire_links')
      .select('id, group_id, title, url, image_url, category, sort_order, created_at, updated_at')
      .eq('group_id', groupId)
      .order('sort_order', { ascending: true })
      .order('created_at', { ascending: true });

    setIsAttireLoading(false);
    if (error) {
      setAttireError(error.message || 'Failed to load attire links.');
      return;
    }

    setAttireLinks((data ?? []) as GroupAttireLinkRow[]);
  }, [groupId]);

  const loadAttireCategories = useCallback(async () => {
    if (!groupId) return;
    setIsAttireCategoriesLoading(true);

    const [defaultResult, customResult] = await Promise.all([
      supabase
        .from('group_attire_default_categories')
        .select('id, name, sort_order, created_at, updated_at')
        .order('sort_order', { ascending: true })
        .order('created_at', { ascending: true }),
      supabase
        .from('group_attire_categories')
        .select('id, group_id, name, created_at, updated_at')
        .eq('group_id', groupId)
        .order('name', { ascending: true }),
    ]);

    setIsAttireCategoriesLoading(false);
    if (defaultResult.error) {
      setAttireError(defaultResult.error.message || 'Failed to load default attire categories.');
      return;
    }
    if (customResult.error) {
      setAttireError(customResult.error.message || 'Failed to load attire categories.');
      return;
    }

    setAttireDefaultCategories(
      ((defaultResult.data ?? []) as GroupAttireDefaultCategoryRow[]).map((category) => ({
        ...category,
        name: sanitizeGroupAttireCategoryName(category.name),
      }))
    );
    setAttireCustomCategories(
      ((customResult.data ?? []) as GroupAttireCategoryRow[]).map((category) => ({
        ...category,
        name: sanitizeGroupAttireCategoryName(category.name),
      }))
    );
  }, [groupId]);

  useEffect(() => {
    if (!groupId || !user?.id || canManageGroups) {
      setCurrentGroupRole(null);
      setIsResolvingGroupAccess(false);
      return;
    }

    let isMounted = true;
    setIsResolvingGroupAccess(true);

    const loadCurrentGroupRole = async () => {
      const { data, error } = await supabase
        .from('group_members')
        .select('role')
        .eq('group_id', groupId)
        .eq('user_id', user.id)
        .maybeSingle();

      if (!isMounted) return;

      if (error) {
        setCurrentGroupRole(null);
        setIsResolvingGroupAccess(false);
        return;
      }

      setCurrentGroupRole(((data as { role?: GroupMemberRole | null } | null)?.role ?? null) as GroupMemberRole | null);
      setIsResolvingGroupAccess(false);
    };

    void loadCurrentGroupRole();

    return () => {
      isMounted = false;
    };
  }, [canManageGroups, groupId, user?.id]);

  useEffect(() => {
    if (!groupId || !canAccessGroupManagement) return;

    const loadGroup = async () => {
      setIsLoadingDetails(true);
      setGroupDetailsError(null);
      const { data, error } = await supabase
        .from('groups')
        .select('name, category, max_members, current_members, settings')
        .eq('id', groupId)
        .single();
      setIsLoadingDetails(false);
      if (error) {
        setGroupDetailsError(error.message || 'Failed to load group details.');
        return;
      }
      const nextName = data?.name ?? '';
      const nextCategory = data?.category || 'other';
      const nextMaxMembers =
        data?.max_members != null && Number.isFinite(data.max_members) ? String(data.max_members) : '';
      const nextCurrentMembers =
        data?.current_members != null && Number.isFinite(data.current_members) ? Number(data.current_members) : 0;

      setGroupName(nextName);
      setGroupCategory(nextCategory);
      setGroupMaxMembers(nextMaxMembers);
      setGroupCurrentMembers(nextCurrentMembers);
      setSettingsName(nextName);
      setSettingsCategory(nextCategory);
      setSettingsMaxMembers(nextMaxMembers);
      setAttireEnabled(parseAttireEnabled(data?.settings));
    };

    void loadGroup();
  }, [groupId, canAccessGroupManagement]);

  const handleSaveGroupSettings = async () => {
    if (!groupId || !canManageGroups) return;

    const trimmedName = settingsName.trim();
    if (!trimmedName) {
      setGroupSettingsError('Group name cannot be empty.');
      setGroupSettingsSuccess(null);
      return;
    }
    if (!GROUP_CATEGORY_OPTIONS.some((option) => option.value === settingsCategory)) {
      setGroupSettingsError('Select a valid category.');
      setGroupSettingsSuccess(null);
      return;
    }

    const trimmedMaxMembers = settingsMaxMembers.trim();
    const parsedMaxMembers = trimmedMaxMembers ? Number(trimmedMaxMembers) : null;
    if (
      parsedMaxMembers !== null &&
      (!Number.isFinite(parsedMaxMembers) || !Number.isInteger(parsedMaxMembers) || parsedMaxMembers <= 0)
    ) {
      setGroupSettingsError('Max members must be a positive whole number or empty.');
      setGroupSettingsSuccess(null);
      return;
    }

    setIsSavingGroupSettings(true);
    setGroupSettingsError(null);
    setGroupSettingsSuccess(null);

    const { data, error: invokeError } = await supabase.functions.invoke('rename-group', {
      body: {
        group_id: groupId,
        name: trimmedName,
        category: settingsCategory,
        max_members: parsedMaxMembers,
      },
    });

    setIsSavingGroupSettings(false);
    if (invokeError || data?.error) {
      setGroupSettingsError(invokeError?.message || data?.error || 'Failed to update group settings.');
      return;
    }

    const nextMaxMembers = parsedMaxMembers != null ? String(parsedMaxMembers) : '';
    setGroupName(trimmedName);
    setGroupCategory(settingsCategory);
    setGroupMaxMembers(nextMaxMembers);
    setSettingsName(trimmedName);
    setSettingsCategory(settingsCategory);
    setSettingsMaxMembers(nextMaxMembers);
    setGroupSettingsSuccess('Group settings updated.');
    queryClient.invalidateQueries({ queryKey: ['groups'] });
  };

  const handleResetGroupSettings = () => {
    setSettingsName(groupName);
    setSettingsCategory(groupCategory);
    setSettingsMaxMembers(groupMaxMembers);
    setGroupSettingsError(null);
    setGroupSettingsSuccess(null);
  };

  const syncGroupSummary = async (members: GroupMemberRow[]) => {
    if (!groupId) return;
    void members;
    const { error: syncError } = await supabase.rpc('sync_group_summary_txn', {
      p_group_id: groupId,
    });
    if (syncError) throw syncError;
  };

  const loadGroupMembers = useCallback(async () => {
    if (!groupId) return null;
    setIsMembersLoading(true);
    setManageError(null);
    const { data: rows, error: membersError } = await supabase
      .from('group_members')
      .select('id, group_id, user_id, role, joined_at, user:users(id, full_name, email)')
      .eq('group_id', groupId);

    setIsMembersLoading(false);
    if (membersError) {
      setManageError(membersError.message || 'Failed to load group members.');
      return null;
    }
    const nextMembers = (rows ?? []) as GroupMemberRow[];
    setGroupMembers(nextMembers);
    return nextMembers;
  }, [groupId]);

  const loadGroupPosts = useCallback(async () => {
    if (!groupId) return;
    setIsPostsLoading(true);
    setPostsError(null);
    const { data, error } = await supabase
      .from('group_posts')
      .select('id, content, author_name, type, status, created_at')
      .eq('group_id', groupId)
      .or('status.is.null,status.eq.published')
      .order('created_at', { ascending: false })
      .limit(25);
    setIsPostsLoading(false);
    if (error) {
      setPostsError(error.message || 'Failed to load group posts.');
      setPosts([]);
      return;
    }
    setPosts((data ?? []) as GroupPostRow[]);
  }, [groupId]);

  const loadGroupMedia = useCallback(async () => {
    if (!groupId) return;
    setIsMediaLoading(true);
    setMediaError(null);
    const { data, error } = await supabase
      .from('group_media')
      .select('id, type, filename, uploaded_by_name, uploaded_at, size, url, storage_bucket, storage_path')
      .eq('group_id', groupId)
      .order('uploaded_at', { ascending: false })
      .limit(25);
    setIsMediaLoading(false);
    if (error) {
      setMediaError(error.message || 'Failed to load group media.');
      setMedia([]);
      return;
    }
    const resolvedMedia = await Promise.all(
      (data ?? []).map(async (item: any) => ({
        ...item,
        url: await resolveGroupMediaUrl({
          url: item.url,
          storageBucket: item.storage_bucket,
          storagePath: item.storage_path,
        }),
      })),
    );
    setMedia(resolvedMedia as GroupMediaRow[]);
  }, [groupId]);

  const loadGroupChats = useCallback(async () => {
    if (!groupId) return;
    setIsChatsLoading(true);
    setChatsError(null);
    const { data, error } = await supabase
      .from('group_chats')
      .select('id, name, description, message_count, updated_at, is_active')
      .eq('group_id', groupId)
      .order('updated_at', { ascending: false })
      .limit(25);
    setIsChatsLoading(false);
    if (error) {
      setChatsError(error.message || 'Failed to load group chats.');
      setChats([]);
      return;
    }
    setChats((data ?? []) as GroupChatRow[]);
  }, [groupId]);

  const loadJoinRequests = useCallback(async () => {
    if (!groupId) return;
    setIsJoinRequestsLoading(true);
    setJoinRequestsError(null);

    const { data, error } = await supabase
      .from('group_invitations')
      .select('id, group_id, invited_user_id, invited_by, status, message, created_at, expires_at')
      .eq('group_id', groupId)
      .eq('status', 'pending')
      .order('created_at', { ascending: false });

    if (error) {
      setIsJoinRequestsLoading(false);
      setJoinRequestsError(error.message || 'Failed to load join requests.');
      setJoinRequests([]);
      setPendingInvitations([]);
      return;
    }

    const pendingRows = (data ?? []) as GroupJoinRequestRow[];
    const pendingRequests = pendingRows.filter(
      (row) => Boolean(row.invited_by) && row.invited_by === row.invited_user_id
    );
    const adminSentInvitations = pendingRows.filter(
      (row) => Boolean(row.invited_by) && row.invited_by !== row.invited_user_id
    );

    const requestsAndInvites = [...pendingRequests, ...adminSentInvitations];

    if (!requestsAndInvites.length) {
      setJoinRequests([]);
      setPendingInvitations([]);
      setIsJoinRequestsLoading(false);
      return;
    }

    const requesterIds = requestsAndInvites
      .map((row) => row.invited_user_id)
      .filter((id, index, allIds) => Boolean(id) && allIds.indexOf(id) === index);
    const { data: userRows, error: usersError } = await supabase
      .from('users')
      .select('id, full_name, email, phone')
      .in('id', requesterIds);

    if (usersError) {
      setJoinRequestsError(usersError.message || 'Could not load requester profiles. Showing requests without profile details.');
      setJoinRequests(
        pendingRequests.map((request) => ({
          ...request,
          requesting_user: null,
        }))
      );
      setPendingInvitations(
        adminSentInvitations.map((request) => ({
          ...request,
          requesting_user: null,
        }))
      );
      setIsJoinRequestsLoading(false);
      return;
    }

    const byUserId = new Map<string, { id: string; full_name?: string | null; email?: string | null; phone?: string | null }>();
    for (const userRow of (userRows ?? []) as SearchUserRow[]) {
      byUserId.set(userRow.id, userRow);
    }

    const joinedRequests = pendingRequests.map((request) => ({
      ...request,
      requesting_user: byUserId.get(request.invited_user_id) ?? null,
    }));
    const joinedInvitations = adminSentInvitations.map((request) => ({
      ...request,
      requesting_user: byUserId.get(request.invited_user_id) ?? null,
    }));

    setJoinRequests(joinedRequests);
    setPendingInvitations(joinedInvitations);
    setIsJoinRequestsLoading(false);
  }, [groupId]);

  useEffect(() => {
    if (!groupId || !canEditAttireContent) return;
    void loadAttireLinks();
    void loadAttireCategories();
  }, [groupId, canEditAttireContent, loadAttireCategories, loadAttireLinks]);

  useEffect(() => {
    const fallbackCategory = attireCategoryOptions[0] ?? FALLBACK_GROUP_ATTIRE_CATEGORIES[0] ?? '';
    if (!fallbackCategory) return;

    const resolvedCategory = attireForm.category
      ? resolveGroupAttireCategoryName(attireForm.category, attireCategoryOptions)
      : fallbackCategory;
    const nextCategory = resolvedCategory || fallbackCategory;

    if (nextCategory !== attireForm.category) {
      setAttireForm((prev) => (prev.category === nextCategory ? prev : { ...prev, category: nextCategory }));
    }
  }, [attireCategoryOptions, attireForm.category]);

  useEffect(() => {
    if (!groupId || !canManageGroups) return;
    void loadGroupMembers();
    void loadGroupPosts();
    void loadGroupMedia();
    void loadGroupChats();
    void loadJoinRequests();
  }, [
    groupId,
    canManageGroups,
    loadGroupChats,
    loadGroupMedia,
    loadGroupMembers,
    loadGroupPosts,
    loadJoinRequests,
  ]);

  useEffect(() => {
    setMembersPage(1);
  }, [groupId]);

  useEffect(() => {
    setMembersPage((prev) => Math.min(prev, membersTotalPages));
  }, [membersTotalPages]);

  useEffect(() => {
    setPostsPage((prev) => Math.min(prev, postsTotalPages));
  }, [postsTotalPages]);

  useEffect(() => {
    setMediaPage((prev) => Math.min(prev, mediaTotalPages));
  }, [mediaTotalPages]);

  useEffect(() => {
    setChatsPage((prev) => Math.min(prev, chatsTotalPages));
  }, [chatsTotalPages]);

  const handleToggleAttireEnabled = async (nextEnabled: boolean) => {
    if (!groupId || !canToggleAttire) return;

    setAttireBusyKey('toggle-attire');
    setAttireError(null);
    setAttireSuccess(null);

    const { error } = await supabase.rpc('set_group_attire_enabled', {
      p_group_id: groupId,
      p_enabled: nextEnabled,
    });

    setAttireBusyKey(null);
    if (error) {
      setAttireError(error.message || 'Failed to update Attire visibility.');
      return;
    }

    setAttireEnabled(nextEnabled);
    setAttireSuccess(nextEnabled ? 'Attire tab enabled for this group.' : 'Attire tab disabled for this group.');
    void queryClient.invalidateQueries({ queryKey: ['groups'] });
  };

  const handleSaveAttireLink = async () => {
    if (!groupId || !canEditAttireContent) return;

    const title = attireForm.title.trim();
    const url = attireForm.url.trim();
    const imageUrl = attireForm.imageUrl.trim();
    const category = resolveGroupAttireCategoryName(attireForm.category, attireCategoryOptions);
    const hasValidCategory = attireCategoryOptions.some(
      (option) => normalizeGroupAttireCategoryName(option) === normalizeGroupAttireCategoryName(category)
    );

    if (!title || !url || !category || !hasValidCategory) {
      setAttireError('Title, link, and a valid category are all required.');
      setAttireSuccess(null);
      return;
    }

    setAttireBusyKey(editingAttireId ? `save:${editingAttireId}` : 'create-attire-link');
    setAttireError(null);
    setAttireSuccess(null);

    if (editingAttireId) {
      const currentLink = attireLinks.find((link) => link.id === editingAttireId);
      const { error } = await supabase
        .from('group_attire_links')
        .update({
          title,
          url,
          image_url: imageUrl || null,
          category,
          sort_order: currentLink?.sort_order ?? 0,
        })
        .eq('id', editingAttireId);

      setAttireBusyKey(null);
      if (error) {
        setAttireError(error.message || 'Failed to update attire link.');
        return;
      }

      setAttireSuccess('Attire link updated.');
    } else {
      const { error } = await supabase.from('group_attire_links').insert({
        group_id: groupId,
        title,
        url,
        image_url: imageUrl || null,
        category,
        sort_order: orderedAttireLinks.length,
      });

      setAttireBusyKey(null);
      if (error) {
        setAttireError(error.message || 'Failed to add attire link.');
        return;
      }

      setAttireSuccess('Attire link added.');
    }

    resetAttireForm();
    await loadAttireLinks();
  };

  const handleAddAttireCategory = async () => {
    if (!groupId || !canEditAttireContent) return;

    const categoryName = sanitizeGroupAttireCategoryName(newAttireCategoryName);
    if (!categoryName) {
      setAttireError('Enter a category name to add it.');
      setAttireSuccess(null);
      return;
    }

    const existingCategory = attireCategoryOptions.find(
      (option) => normalizeGroupAttireCategoryName(option) === normalizeGroupAttireCategoryName(categoryName)
    );
    if (existingCategory) {
      setAttireForm((prev) => ({ ...prev, category: existingCategory }));
      setNewAttireCategoryName('');
      setAttireError(null);
      setAttireSuccess('Category already exists and has been selected.');
      return;
    }

    setAttireBusyKey('create-attire-category');
    setAttireError(null);
    setAttireSuccess(null);

    const { error } = await supabase.from('group_attire_categories').insert({
      group_id: groupId,
      name: categoryName,
    });

    setAttireBusyKey(null);
    if (error) {
      setAttireError(error.message || 'Failed to add attire category.');
      return;
    }

    setNewAttireCategoryName('');
    setAttireForm((prev) => ({ ...prev, category: categoryName }));
    await loadAttireCategories();
    setAttireSuccess('Attire category added.');
  };

  const handleAddDefaultAttireCategory = async () => {
    if (!canEditAttireContent) return;

    const categoryName = sanitizeGroupAttireCategoryName(newDefaultAttireCategoryName);
    if (!categoryName) {
      setAttireError('Enter a default category name to add it.');
      setAttireSuccess(null);
      return;
    }

    const existingCategory = attireDefaultCategories.find(
      (option) => normalizeGroupAttireCategoryName(option.name) === normalizeGroupAttireCategoryName(categoryName)
    );
    if (existingCategory) {
      setNewDefaultAttireCategoryName('');
      setAttireError(null);
      setAttireSuccess('Default category already exists.');
      return;
    }

    setAttireBusyKey('create-default-attire-category');
    setAttireError(null);
    setAttireSuccess(null);

    const nextSortOrder =
      attireDefaultCategories.reduce((maxOrder, category) => Math.max(maxOrder, category.sort_order ?? -1), -1) + 1;
    const { error } = await supabase.from('group_attire_default_categories').insert({
      name: categoryName,
      sort_order: nextSortOrder,
    });

    setAttireBusyKey(null);
    if (error) {
      setAttireError(error.message || 'Failed to add default attire category.');
      return;
    }

    setNewDefaultAttireCategoryName('');
    await loadAttireCategories();
    setAttireSuccess('Default attire category added.');
  };

  const handleDeleteDefaultAttireCategory = async (category: GroupAttireDefaultCategoryRow) => {
    if (!canEditAttireContent) return;
    if (!window.confirm(`Delete default category "${category.name}"? Existing attire links using it will be changed to Other.`)) {
      return;
    }

    setAttireBusyKey(`delete-default-attire-category:${category.id}`);
    setAttireError(null);
    setAttireSuccess(null);

    const { data, error } = await supabase.rpc('delete_group_attire_default_category', {
      p_category_id: category.id,
    });

    setAttireBusyKey(null);
    if (error) {
      setAttireError(error.message || 'Failed to delete default attire category.');
      return;
    }

    const convertedLinks =
      typeof data === 'object' && data && 'convertedLinks' in data && typeof data.convertedLinks === 'number'
        ? data.convertedLinks
        : 0;

    await Promise.all([loadAttireCategories(), loadAttireLinks()]);
    setAttireSuccess(
      convertedLinks > 0
        ? `Default category deleted. ${convertedLinks} attire link${convertedLinks === 1 ? '' : 's'} moved to Other.`
        : 'Default attire category deleted.'
    );
  };

  const handleAttireImageUpload = async (file?: File | null) => {
    if (!file || !groupId || !canEditAttireContent) return;

    setIsUploadingAttireImage(true);
    setAttireError(null);
    setAttireSuccess(null);
    try {
      const imageUrl = await uploadAttireImage(file, groupId);
      setAttireForm((prev) => ({ ...prev, imageUrl }));
    } catch (error) {
      console.error(error);
      setAttireError(error instanceof Error ? error.message : 'Could not upload attire image.');
    } finally {
      setIsUploadingAttireImage(false);
    }
  };

  const handleEditAttireLink = (link: GroupAttireLinkRow) => {
    setEditingAttireId(link.id);
    setAttireForm({
      title: link.title,
      url: link.url,
      imageUrl: link.image_url ?? '',
      category: resolveGroupAttireCategoryName(link.category, attireCategoryOptions),
    });
    setAttireError(null);
    setAttireSuccess(null);
  };

  const handleDeleteAttireLink = async (link: GroupAttireLinkRow) => {
    if (!groupId || !canEditAttireContent) return;
    if (!window.confirm(`Delete "${link.title}"?`)) return;

    setAttireBusyKey(`delete:${link.id}`);
    setAttireError(null);
    setAttireSuccess(null);

    const { error } = await supabase.from('group_attire_links').delete().eq('id', link.id);

    setAttireBusyKey(null);
    if (error) {
      setAttireError(error.message || 'Failed to delete attire link.');
      return;
    }

    setAttireSuccess('Attire link deleted.');
    if (editingAttireId === link.id) {
      resetAttireForm();
    }
    await loadAttireLinks();
  };

  const handleMoveAttireLink = async (index: number, direction: -1 | 1) => {
    if (!groupId || !canEditAttireContent) return;
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= orderedAttireLinks.length) return;

    const nextLinks = [...orderedAttireLinks];
    const [movedLink] = nextLinks.splice(index, 1);
    nextLinks.splice(targetIndex, 0, movedLink);
    const nextOrderedLinks = nextLinks.map((link, nextIndex) => ({
      ...link,
      sort_order: nextIndex,
    }));

    setAttireBusyKey(`move:${movedLink.id}`);
    setAttireError(null);
    setAttireSuccess(null);

    const results = await Promise.all(
      nextOrderedLinks.map((link) =>
        supabase.from('group_attire_links').update({ sort_order: link.sort_order ?? 0 }).eq('id', link.id),
      ),
    );

    setAttireBusyKey(null);
    const failedResult = results.find((result) => result.error);
    if (failedResult?.error) {
      setAttireError(failedResult.error.message || 'Failed to reorder attire links.');
      await loadAttireLinks();
      return;
    }

    setAttireLinks(nextOrderedLinks);
    setAttireSuccess('Attire link order updated.');
  };

  const handleSearchUsers = async () => {
    if (!groupId) return;

    const normalizedSearch = searchQuery.trim();
    if (normalizedSearch.length < 2) {
      setSearchError('Search at least 2 characters.');
      setSearchResults([]);
      return;
    }

    setSearchError(null);
    setIsSearchingUsers(true);
    const { data: users, error: usersError } = await supabase
      .from('users_directory')
      .select('id, full_name, email, phone')
      .or(`full_name.ilike.%${normalizedSearch}%,email.ilike.%${normalizedSearch}%,phone.ilike.%${normalizedSearch}%`)
      .order('full_name', { ascending: true })
      .limit(15);

    setIsSearchingUsers(false);
    if (usersError) {
      setSearchError(usersError.message || 'Failed to search users.');
      setSearchResults([]);
      return;
    }

    const currentMemberIds = new Set(groupMembers.map((member) => member.user_id));
    const filtered = ((users ?? []) as SearchUserRow[]).filter((row) => !currentMemberIds.has(row.id));
    setSearchResults(filtered);
    if (!filtered.length) {
      setSearchError('No matching users available to add.');
    }
  };

  const handleAddMember = async (candidate: SearchUserRow) => {
    if (!groupId) return;
    const actionKey = `add:${candidate.id}`;
    setBusyActionKey(actionKey);
    setManageError(null);
    setManageSuccess(null);

    const { data, error } = await supabase.rpc('add_group_member_txn', {
      p_group_id: groupId,
      p_user_id: candidate.id,
      p_role: 'member',
      p_resolve_pending_invitations: true,
    });
    if (error) {
      setManageError(error.message || 'Failed to add member.');
      setBusyActionKey(null);
      return;
    }

    const resolution = Array.isArray(data) ? data[0] : null;

    await loadGroupMembers();
    await loadJoinRequests();

    const resolvedCount = Number(resolution?.resolved_invitation_count ?? 0);
    setManageSuccess(
      resolvedCount > 0 ? 'Member added and pending join request resolved.' : 'Member added.'
    );
    setBusyActionKey(null);
    setSearchResults((previous) => previous.filter((row) => row.id !== candidate.id));
    queryClient.invalidateQueries({ queryKey: ['groups'] });
  };

  const handleToggleAdmin = async (member: GroupMemberRow) => {
    if (!groupId) return;
    const demotingLastAdmin = member.role === 'admin' && adminCount <= 1;
    if (demotingLastAdmin) {
      setManageError('Group must have at least one admin.');
      return;
    }
    const nextRole: GroupMemberRole = member.role === 'admin' ? 'member' : 'admin';
    const actionKey = `role:${member.user_id}`;
    setBusyActionKey(actionKey);
    setManageError(null);
    setManageSuccess(null);

    const { error: updateError } = await supabase
      .from('group_members')
      .update({ role: nextRole })
      .eq('group_id', groupId)
      .eq('user_id', member.user_id);
    if (updateError) {
      setManageError(updateError.message || 'Failed to update member role.');
      setBusyActionKey(null);
      return;
    }

    const refreshedMembers = await loadGroupMembers();
    if (refreshedMembers) {
      await syncGroupSummary(refreshedMembers).catch((syncError: unknown) => {
        const message = syncError instanceof Error ? syncError.message : 'Failed to sync group admins.';
        setManageError(message);
      });
    }
    setManageSuccess(nextRole === 'admin' ? 'Member promoted to admin.' : 'Admin rights removed.');
    setBusyActionKey(null);
    queryClient.invalidateQueries({ queryKey: ['groups'] });
  };

  const handleRemoveMember = async (member: GroupMemberRow) => {
    if (!groupId) return;
    if (member.role === 'admin' && adminCount <= 1) {
      setManageError('Cannot remove the last admin. Promote another admin first.');
      return;
    }
    const actionKey = `remove:${member.user_id}`;
    setBusyActionKey(actionKey);
    setManageError(null);
    setManageSuccess(null);

    const { error: deleteError } = await supabase
      .from('group_members')
      .delete()
      .eq('group_id', groupId)
      .eq('user_id', member.user_id);
    if (deleteError) {
      setManageError(deleteError.message || 'Failed to remove member.');
      setBusyActionKey(null);
      return;
    }

    const refreshedMembers = await loadGroupMembers();
    if (refreshedMembers) {
      await syncGroupSummary(refreshedMembers).catch((syncError: unknown) => {
        const message = syncError instanceof Error ? syncError.message : 'Failed to sync group summary.';
        setManageError(message);
      });
    }
    setManageSuccess('Member removed.');
    setBusyActionKey(null);
    queryClient.invalidateQueries({ queryKey: ['groups'] });
  };

  const handleDeleteGroup = async () => {
    if (!groupId) return;
    if (deleteConfirmationText.trim() !== confirmationTarget) {
      setManageError(`Type "${confirmationTarget}" to confirm deletion.`);
      return;
    }
    setIsDeletingGroup(true);
    setManageError(null);
    setManageSuccess(null);
    const { error: deleteError } = await supabase.rpc('safe_delete_group', {
      group_id_to_delete: groupId,
    });

    setIsDeletingGroup(false);
    if (deleteError) {
      setManageError(deleteError.message || 'Failed to delete group.');
      return;
    }

    queryClient.invalidateQueries({ queryKey: ['groups'] });
    navigate('/groups');
  };

  const handleApproveJoinRequest = async (request: GroupJoinRequestRow) => {
    if (!groupId) return;
    const actionKey = `join:approve:${request.id}`;
    setBusyActionKey(actionKey);
    setManageError(null);
    setManageSuccess(null);

    const { data, error } = await supabase.rpc('respond_to_group_join_request_txn', {
      p_invitation_id: request.id,
      p_status: 'accepted',
    });

    if (error) {
      setManageError(error.message || 'Failed to approve join request.');
      setBusyActionKey(null);
      return;
    }

    const resolution = Array.isArray(data) ? data[0] : null;
    if (!resolution?.group_id || !resolution?.invited_user_id) {
      setManageError('Join request no longer exists.');
      setBusyActionKey(null);
      return;
    }

    if (String(resolution.outcome || '').toLowerCase() === 'conflict') {
      await loadJoinRequests();
      setManageError('This join request was already handled by another admin.');
      setBusyActionKey(null);
      return;
    }

    await loadGroupMembers();
    await loadJoinRequests();

    setManageSuccess('Join request approved.');
    setBusyActionKey(null);
    queryClient.invalidateQueries({ queryKey: ['groups'] });
  };

  const handleDeclineJoinRequest = async (request: GroupJoinRequestRow) => {
    const actionKey = `join:decline:${request.id}`;
    setBusyActionKey(actionKey);
    setManageError(null);
    setManageSuccess(null);

    const { data, error } = await supabase.rpc('respond_to_group_join_request_txn', {
      p_invitation_id: request.id,
      p_status: 'declined',
    });

    if (error) {
      setManageError(error.message || 'Failed to decline join request.');
      setBusyActionKey(null);
      return;
    }

    const resolution = Array.isArray(data) ? data[0] : null;
    if (!resolution?.group_id || !resolution?.invited_user_id) {
      setManageError('Join request no longer exists.');
      setBusyActionKey(null);
      return;
    }

    if (String(resolution.outcome || '').toLowerCase() === 'conflict') {
      await loadJoinRequests();
      setManageError('This join request was already handled by another admin.');
      setBusyActionKey(null);
      return;
    }

    await loadJoinRequests();
    setManageSuccess('Join request declined.');
    setBusyActionKey(null);
  };

  if (isResolvingGroupAccess) {
    return (
      <Page>
        <HeaderTitle>Manage Group</HeaderTitle>
        <MutedText>Checking group access...</MutedText>
      </Page>
    );
  }

  if (!canAccessGroupManagement) {
    return <Navigate to={groupId ? `/groups/${groupId}` : '/groups'} replace />;
  }

  if (!groupId) {
    return (
      <Page>
        <HelperText $tone="error">Missing group id.</HelperText>
      </Page>
    );
  }

  return (
    <Page>
      <HeaderRow>
        <div>
          <HeaderTitle>Manage Group</HeaderTitle>
          <HeaderMeta>{isLoadingDetails ? 'Loading group...' : groupName || groupId}</HeaderMeta>
          {groupDetailsError ? <HelperText $tone="error">{groupDetailsError}</HelperText> : null}
        </div>
        <HeaderActions>
          <BackLink to={`/groups/${groupId}`}>
            <Button type="button">View Group</Button>
          </BackLink>
          <BackLink to="/groups">
            <Button type="button">Back to Groups</Button>
          </BackLink>
        </HeaderActions>
      </HeaderRow>

      {canManageGroups ? (
        <>
      <Card>
        <SectionTitle>Group Settings</SectionTitle>
        <FormGrid>
          <FormField>
            Group Name
            <Input
              value={settingsName}
              onChange={(event) => setSettingsName(event.target.value)}
              maxLength={255}
              disabled={isSavingGroupSettings || isLoadingDetails}
            />
          </FormField>
          <FormField>
            Category
            <Select
              value={settingsCategory}
              onChange={(event) => setSettingsCategory(event.target.value)}
              disabled={isSavingGroupSettings || isLoadingDetails}
            >
              {GROUP_CATEGORY_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
          </FormField>
          <FormField>
            Max Members (optional)
            <Input
              value={settingsMaxMembers}
              onChange={(event) => setSettingsMaxMembers(event.target.value)}
              placeholder="e.g. 25"
              inputMode="numeric"
              disabled={isSavingGroupSettings || isLoadingDetails}
            />
            <MutedText style={{ margin: 0 }}>
              Current members: {groupCurrentMembers}
              {groupMaxMembers ? ` / ${groupMaxMembers}` : ''}
            </MutedText>
          </FormField>
        </FormGrid>
        <InlineActions>
          <PrimaryButton
            type="button"
            disabled={isSavingGroupSettings || isLoadingDetails}
            onClick={() => void handleSaveGroupSettings()}
          >
            {isSavingGroupSettings ? 'Saving...' : 'Save Settings'}
          </PrimaryButton>
          <SecondaryButton
            type="button"
            disabled={isSavingGroupSettings || isLoadingDetails}
            onClick={handleResetGroupSettings}
          >
            Reset
          </SecondaryButton>
        </InlineActions>
        {groupSettingsError ? <HelperText $tone="error">{groupSettingsError}</HelperText> : null}
        {groupSettingsSuccess ? <HelperText $tone="success">{groupSettingsSuccess}</HelperText> : null}
      </Card>

      <Card>
        <SectionTitle>Add Member</SectionTitle>
        <SearchRow>
          <SearchInput
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            placeholder="Search users by name, email, or phone"
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                void handleSearchUsers();
              }
            }}
          />
          <Button type="button" onClick={handleSearchUsers} disabled={isSearchingUsers}>
            {isSearchingUsers ? 'Searching...' : 'Search'}
          </Button>
        </SearchRow>
        {searchError ? <HelperText $tone="error">{searchError}</HelperText> : null}
        {searchResults.length ? (
          <MemberList>
            {searchResults.map((userRow) => {
              const key = `add:${userRow.id}`;
              return (
                <MemberItem key={userRow.id}>
                  <MemberMain>
                    <strong>{userRow.full_name?.trim() || 'Unnamed user'}</strong>
                    <MetaLine>{userRow.email || userRow.phone || '-'}</MetaLine>
                  </MemberMain>
                  <MemberActions>
                    <Button type="button" disabled={busyActionKey === key} onClick={() => handleAddMember(userRow)}>
                      {busyActionKey === key ? 'Adding...' : 'Add'}
                    </Button>
                  </MemberActions>
                </MemberItem>
              );
            })}
          </MemberList>
        ) : null}
      </Card>

      <Card>
        <SectionTitle>Join Requests ({joinRequests.length})</SectionTitle>
        {isJoinRequestsLoading ? <MutedText>Loading join requests...</MutedText> : null}
        {joinRequestsError ? <HelperText $tone="error">{joinRequestsError}</HelperText> : null}
        {!isJoinRequestsLoading && !joinRequestsError && !joinRequests.length ? (
          <MutedText>No pending join requests.</MutedText>
        ) : null}
        {!isJoinRequestsLoading && joinRequests.length ? (
          <MemberList>
            {joinRequests.map((request) => {
              const approveKey = `join:approve:${request.id}`;
              const declineKey = `join:decline:${request.id}`;
              const requesterName = request.requesting_user?.full_name?.trim() || 'Unnamed user';
              const requesterMeta =
                request.requesting_user?.email || request.requesting_user?.phone || request.invited_user_id;

              return (
                <MemberItem key={request.id}>
                  <MemberMain>
                    <strong>{requesterName}</strong>
                    <MetaLine>{requesterMeta}</MetaLine>
                    <MetaLine>Requested {formatDate(request.created_at)}</MetaLine>
                    {request.message?.trim() ? <MutedText>"{request.message.trim()}"</MutedText> : null}
                  </MemberMain>
                  <MemberActions>
                    <DangerButton
                      type="button"
                      disabled={busyActionKey === approveKey || busyActionKey === declineKey}
                      onClick={() => handleDeclineJoinRequest(request)}
                    >
                      {busyActionKey === declineKey ? 'Declining...' : 'Decline'}
                    </DangerButton>
                    <Button
                      type="button"
                      disabled={busyActionKey === approveKey || busyActionKey === declineKey}
                      onClick={() => handleApproveJoinRequest(request)}
                    >
                      {busyActionKey === approveKey ? 'Approving...' : 'Approve'}
                    </Button>
                  </MemberActions>
                </MemberItem>
              );
            })}
          </MemberList>
        ) : null}
      </Card>

      <Card>
        <SectionTitle>Pending Invitations ({pendingInvitations.length})</SectionTitle>
        {isJoinRequestsLoading ? <MutedText>Loading invitations...</MutedText> : null}
        {joinRequestsError ? <HelperText $tone="error">{joinRequestsError}</HelperText> : null}
        {!isJoinRequestsLoading && !joinRequestsError && !pendingInvitations.length ? (
          <MutedText>No pending invitations.</MutedText>
        ) : null}
        {!isJoinRequestsLoading && pendingInvitations.length ? (
          <MemberList>
            {pendingInvitations.map((request) => {
              const approveKey = `join:approve:${request.id}`;
              const declineKey = `join:decline:${request.id}`;
              const requesterName = request.requesting_user?.full_name?.trim() || 'Unnamed user';
              const requesterMeta =
                request.requesting_user?.email || request.requesting_user?.phone || request.invited_user_id;

              return (
                <MemberItem key={request.id}>
                  <MemberMain>
                    <strong>{requesterName}</strong>
                    <MetaLine>{requesterMeta}</MetaLine>
                    <MetaLine>Invited {formatDate(request.created_at)}</MetaLine>
                    {request.message?.trim() ? <MutedText>"{request.message.trim()}"</MutedText> : null}
                  </MemberMain>
                  <MemberActions>
                    <DangerButton
                      type="button"
                      disabled={busyActionKey === approveKey || busyActionKey === declineKey}
                      onClick={() => handleDeclineJoinRequest(request)}
                    >
                      {busyActionKey === declineKey ? 'Declining...' : 'Decline'}
                    </DangerButton>
                    <Button
                      type="button"
                      disabled={busyActionKey === approveKey || busyActionKey === declineKey}
                      onClick={() => handleApproveJoinRequest(request)}
                    >
                      {busyActionKey === approveKey ? 'Approving...' : 'Approve'}
                    </Button>
                  </MemberActions>
                </MemberItem>
              );
            })}
          </MemberList>
        ) : null}
      </Card>

      <Card>
        <SectionTitle>Members ({groupMembers.length})</SectionTitle>
        {isMembersLoading ? <MutedText>Loading members...</MutedText> : null}
        {!isMembersLoading && !orderedMembers.length ? <MutedText>No members found.</MutedText> : null}
        {!isMembersLoading && orderedMembers.length ? (
          <MemberList>
            {pagedMembers.map((member) => {
              const roleActionKey = `role:${member.user_id}`;
              const removeActionKey = `remove:${member.user_id}`;
              const displayName = member.user?.full_name?.trim() || 'Unnamed user';
              return (
                <MemberItem key={member.id}>
                  <MemberMain>
                    <strong>{displayName}</strong>
                    <MetaLine>{member.user?.email || member.user_id}</MetaLine>
                  </MemberMain>
                  <MemberActions>
                    <RoleBadge $role={member.role}>{member.role}</RoleBadge>
                    <Button
                      type="button"
                      disabled={busyActionKey === roleActionKey || busyActionKey === removeActionKey}
                      onClick={() => handleToggleAdmin(member)}
                    >
                      {member.role === 'admin' ? 'Remove Admin' : 'Make Admin'}
                    </Button>
                    <DangerButton
                      type="button"
                      disabled={busyActionKey === roleActionKey || busyActionKey === removeActionKey}
                      onClick={() => handleRemoveMember(member)}
                    >
                      {busyActionKey === removeActionKey ? 'Removing...' : 'Remove'}
                    </DangerButton>
                  </MemberActions>
                </MemberItem>
              );
            })}
          </MemberList>
        ) : null}
        {!isMembersLoading && orderedMembers.length > MEMBERS_PER_PAGE ? (
          <PaginationRow>
            <Button type="button" onClick={() => setMembersPage((prev) => Math.max(1, prev - 1))} disabled={membersPage === 1}>
              Prev
            </Button>
            <PaginationInfo>
              {membersPage} / {membersTotalPages}
            </PaginationInfo>
            <Button
              type="button"
              onClick={() => setMembersPage((prev) => Math.min(membersTotalPages, prev + 1))}
              disabled={membersPage === membersTotalPages}
            >
              Next
            </Button>
          </PaginationRow>
        ) : null}
      </Card>

      <Grid>
        <Card>
          <SectionTitle>Group Posts ({posts.length})</SectionTitle>
          {isPostsLoading ? <MutedText>Loading posts...</MutedText> : null}
          {postsError ? <HelperText $tone="error">{postsError}</HelperText> : null}
          {!isPostsLoading && !postsError && !posts.length ? <MutedText>No posts yet.</MutedText> : null}
          {!isPostsLoading && posts.length ? (
            <List>
              {pagedPosts.map((post) => (
                <ListItem key={post.id}>
                  <strong>{post.author_name?.trim() || 'Unknown author'}</strong>
                  <MetaLine>{post.type || 'post'} • {post.status || 'published'} • {formatDate(post.created_at)}</MetaLine>
                  <MutedText>{post.content?.trim() || '(No text content)'}</MutedText>
                </ListItem>
              ))}
            </List>
          ) : null}
          {!isPostsLoading && posts.length > POSTS_PER_PAGE ? (
            <PaginationRow>
              <Button type="button" onClick={() => setPostsPage((prev) => Math.max(1, prev - 1))} disabled={postsPage === 1}>
                Prev
              </Button>
              <PaginationInfo>
                {postsPage} / {postsTotalPages}
              </PaginationInfo>
              <Button
                type="button"
                onClick={() => setPostsPage((prev) => Math.min(postsTotalPages, prev + 1))}
                disabled={postsPage === postsTotalPages}
              >
                Next
              </Button>
            </PaginationRow>
          ) : null}
        </Card>

        <Card>
          <SectionTitle>Group Media ({media.length})</SectionTitle>
          {isMediaLoading ? <MutedText>Loading media...</MutedText> : null}
          {mediaError ? <HelperText $tone="error">{mediaError}</HelperText> : null}
          {!isMediaLoading && !mediaError && !media.length ? <MutedText>No media uploaded yet.</MutedText> : null}
          {!isMediaLoading && media.length ? (
            <List>
              {pagedMedia.map((item) => (
                <ListItem key={item.id}>
                  <strong>{item.filename || 'Untitled file'}</strong>
                  <MetaLine>
                    {item.type || 'file'} • {formatFileSize(item.size)} • {formatDate(item.uploaded_at)}
                  </MetaLine>
                  <MutedText>Uploaded by {item.uploaded_by_name?.trim() || 'Unknown user'}</MutedText>
                  {item.url ? (
                    <a href={item.url} target="_blank" rel="noreferrer">
                      Open file
                    </a>
                  ) : null}
                </ListItem>
              ))}
            </List>
          ) : null}
          {!isMediaLoading && media.length > MEDIA_PER_PAGE ? (
            <PaginationRow>
              <Button type="button" onClick={() => setMediaPage((prev) => Math.max(1, prev - 1))} disabled={mediaPage === 1}>
                Prev
              </Button>
              <PaginationInfo>
                {mediaPage} / {mediaTotalPages}
              </PaginationInfo>
              <Button
                type="button"
                onClick={() => setMediaPage((prev) => Math.min(mediaTotalPages, prev + 1))}
                disabled={mediaPage === mediaTotalPages}
              >
                Next
              </Button>
            </PaginationRow>
          ) : null}
        </Card>
      </Grid>

      <Card>
        <SectionTitle>Group Chats ({chats.length})</SectionTitle>
        {isChatsLoading ? <MutedText>Loading chats...</MutedText> : null}
        {chatsError ? <HelperText $tone="error">{chatsError}</HelperText> : null}
        {!isChatsLoading && !chatsError && !chats.length ? <MutedText>No chats created yet.</MutedText> : null}
        {!isChatsLoading && chats.length ? (
          <List>
            {pagedChats.map((chat) => (
              <ListItem key={chat.id}>
                <strong>{chat.name?.trim() || 'Unnamed chat'}</strong>
                <MetaLine>
                  {(chat.is_active ?? true) ? 'active' : 'inactive'} • {chat.message_count ?? 0} messages • updated{' '}
                  {formatDate(chat.updated_at)}
                </MetaLine>
                <MutedText>{chat.description?.trim() || '(No description)'}</MutedText>
              </ListItem>
            ))}
          </List>
        ) : null}
        {!isChatsLoading && chats.length > CHATS_PER_PAGE ? (
          <PaginationRow>
            <Button type="button" onClick={() => setChatsPage((prev) => Math.max(1, prev - 1))} disabled={chatsPage === 1}>
              Prev
            </Button>
            <PaginationInfo>
              {chatsPage} / {chatsTotalPages}
            </PaginationInfo>
            <Button
              type="button"
              onClick={() => setChatsPage((prev) => Math.min(chatsTotalPages, prev + 1))}
              disabled={chatsPage === chatsTotalPages}
            >
              Next
            </Button>
          </PaginationRow>
        ) : null}
      </Card>

      <Card>
        <SectionTitle>Attire</SectionTitle>
        <MutedText>
          Enable the mobile Attire tab for this group and manage the categorized links and optional images that appear there.
        </MutedText>
        <InlineActions>
          <ToggleRow>
            <input
              type="checkbox"
              checked={attireEnabled}
              onChange={(event) => {
                void handleToggleAttireEnabled(event.target.checked);
              }}
              disabled={!canToggleAttire || attireBusyKey === 'toggle-attire'}
            />
            <span>{attireEnabled ? 'Attire tab enabled' : 'Attire tab disabled'}</span>
          </ToggleRow>
        </InlineActions>
        {!canToggleAttire ? (
          <MutedText>Only super admins can enable or disable the Attire tab for a group.</MutedText>
        ) : null}
        {!canEditAttireContent ? (
          <MutedText>Only staff managers or this group's admins can edit Attire links and categories.</MutedText>
        ) : null}
        {attireError ? <HelperText $tone="error">{attireError}</HelperText> : null}
        {attireSuccess ? <HelperText $tone="success">{attireSuccess}</HelperText> : null}

        <div style={{ marginTop: '1rem' }}>
          <SectionTitle>Default Categories</SectionTitle>
          <MutedText>
            These shared defaults appear in the attire dropdown for every group. Deleting one changes existing attire
            links using it to <strong>Other</strong>.
          </MutedText>
          <InlineActions style={{ marginTop: '0.75rem' }}>
            <Input
              value={newDefaultAttireCategoryName}
              onChange={(event) => setNewDefaultAttireCategoryName(event.target.value)}
              placeholder="Add shared default category"
              disabled={
                !canEditAttireContent ||
                isAttireCategoriesLoading ||
                attireBusyKey === 'create-default-attire-category'
              }
              style={{ flex: 1, minWidth: '220px' }}
            />
            <SecondaryButton
              type="button"
              onClick={() => void handleAddDefaultAttireCategory()}
              disabled={
                !canEditAttireContent ||
                isAttireCategoriesLoading ||
                attireBusyKey === 'create-default-attire-category' ||
                !sanitizeGroupAttireCategoryName(newDefaultAttireCategoryName)
              }
            >
              {attireBusyKey === 'create-default-attire-category' ? 'Adding...' : 'Add default'}
            </SecondaryButton>
          </InlineActions>
          <List style={{ marginTop: '0.75rem', maxHeight: 'unset' }}>
            {attireDefaultCategories.map((category) => {
              const isOtherCategory = normalizeGroupAttireCategoryName(category.name) === 'other';
              const isDeleting = attireBusyKey === `delete-default-attire-category:${category.id}`;

              return (
                <ListItem key={category.id}>
                  <strong>{category.name}</strong>
                  <MetaLine>
                    {isOtherCategory ? 'Required fallback category' : 'Shared default category'}
                  </MetaLine>
                  {canEditAttireContent ? (
                    <ActionButtons>
                      <DangerButton
                        type="button"
                        onClick={() => void handleDeleteDefaultAttireCategory(category)}
                        disabled={isDeleting || isOtherCategory}
                      >
                        {isDeleting ? 'Deleting...' : isOtherCategory ? 'Protected' : 'Delete'}
                      </DangerButton>
                    </ActionButtons>
                  ) : null}
                </ListItem>
              );
            })}
          </List>
        </div>

        <FormGrid style={{ marginTop: '1rem' }}>
          <FormField>
            Link title
            <Input
              value={attireForm.title}
              onChange={(event) => setAttireForm((prev) => ({ ...prev, title: event.target.value }))}
              placeholder="Sunday Uniform Guide"
              disabled={!canEditAttireContent}
            />
          </FormField>
          <FormField>
            Link URL
            <Input
              value={attireForm.url}
              onChange={(event) => setAttireForm((prev) => ({ ...prev, url: event.target.value }))}
              placeholder="https://example.com/attire-guide"
              disabled={!canEditAttireContent}
            />
          </FormField>
          <FormField>
            Category
            <Select
              value={attireForm.category}
              onChange={(event) => setAttireForm((prev) => ({ ...prev, category: event.target.value }))}
              disabled={
                !canEditAttireContent ||
                isAttireCategoriesLoading ||
                attireBusyKey === 'create-attire-category' ||
                attireBusyKey === 'create-default-attire-category'
              }
            >
              {attireCategoryOptions.map((category) => (
                <option key={category} value={category}>
                  {category}
                </option>
              ))}
            </Select>
            <MutedText>Choose a default category or add one for this group.</MutedText>
            {canEditAttireContent ? (
              <InlineActions style={{ marginTop: '0.5rem' }}>
                <Input
                  value={newAttireCategoryName}
                  onChange={(event) => setNewAttireCategoryName(event.target.value)}
                  placeholder="Add another category"
                  disabled={
                    isAttireCategoriesLoading ||
                    attireBusyKey === 'create-attire-category' ||
                    attireBusyKey === 'create-default-attire-category'
                  }
                  style={{ flex: 1, minWidth: '180px' }}
                />
                <SecondaryButton
                  type="button"
                  onClick={() => void handleAddAttireCategory()}
                  disabled={
                    isAttireCategoriesLoading ||
                    attireBusyKey === 'create-attire-category' ||
                    attireBusyKey === 'create-default-attire-category' ||
                    !sanitizeGroupAttireCategoryName(newAttireCategoryName)
                  }
                >
                  {attireBusyKey === 'create-attire-category' ? 'Adding...' : 'Add category'}
                </SecondaryButton>
              </InlineActions>
            ) : null}
          </FormField>
          <FormField>
            Optional image
            {attireForm.imageUrl ? (
              <img
                src={attireForm.imageUrl}
                alt="Attire link preview"
                style={{
                  width: '100%',
                  maxWidth: '220px',
                  aspectRatio: '1 / 1',
                  objectFit: 'cover',
                  borderRadius: '12px',
                  border: '1px solid var(--outline)',
                  background: 'var(--surfaceVariant)',
                }}
              />
            ) : (
              <MutedText>No image uploaded yet.</MutedText>
            )}
            <input
              type="file"
              accept="image/*"
              disabled={!canEditAttireContent || isUploadingAttireImage}
              onChange={(event) => {
                void handleAttireImageUpload(event.target.files?.[0] ?? null);
                event.currentTarget.value = '';
              }}
            />
            {attireForm.imageUrl ? (
              <InlineActions style={{ marginTop: '0.5rem' }}>
                <SecondaryButton
                  type="button"
                  onClick={() => setAttireForm((prev) => ({ ...prev, imageUrl: '' }))}
                  disabled={!canEditAttireContent || isUploadingAttireImage}
                >
                  Remove image
                </SecondaryButton>
              </InlineActions>
            ) : null}
            {isUploadingAttireImage ? <MutedText>Uploading image...</MutedText> : null}
          </FormField>
        </FormGrid>
        {canEditAttireContent ? (
          <InlineActions>
            <PrimaryButton
              type="button"
              onClick={() => void handleSaveAttireLink()}
              disabled={
                isUploadingAttireImage ||
                attireBusyKey?.startsWith('save:') ||
                attireBusyKey === 'create-attire-link' ||
                attireBusyKey === 'create-attire-category' ||
                attireBusyKey === 'create-default-attire-category'
              }
            >
              {editingAttireId
                ? attireBusyKey?.startsWith('save:')
                  ? 'Saving...'
                  : 'Save Link'
                : attireBusyKey === 'create-attire-link'
                  ? 'Adding...'
                  : 'Add Link'}
            </PrimaryButton>
            {editingAttireId ? (
              <SecondaryButton type="button" onClick={resetAttireForm}>
                Cancel Edit
              </SecondaryButton>
            ) : null}
          </InlineActions>
        ) : null}

        <div style={{ marginTop: '1rem' }}>
          {isAttireLoading ? <MutedText>Loading attire links...</MutedText> : null}
          {!isAttireLoading && !orderedAttireLinks.length ? <MutedText>No attire links added yet.</MutedText> : null}
          {!isAttireLoading && orderedAttireLinks.length ? (
            <List>
              {orderedAttireLinks.map((link, index) => (
                <ListItem key={link.id}>
                  {link.image_url ? (
                    <img
                      src={link.image_url}
                      alt={link.title.trim() || 'Attire link'}
                      style={{
                        width: '88px',
                        height: '88px',
                        objectFit: 'cover',
                        borderRadius: '12px',
                        border: '1px solid var(--outline)',
                        background: 'var(--surface)',
                      }}
                    />
                  ) : null}
                  <strong>{link.title.trim() || 'Untitled link'}</strong>
                  <MetaLine>
                    {resolveGroupAttireCategoryName(link.category, attireCategoryOptions)} • updated{' '}
                    {formatDate(link.updated_at ?? link.created_at)}
                  </MetaLine>
                  <a href={normalizeExternalUrl(link.url)} target="_blank" rel="noreferrer">
                    {link.url}
                  </a>
                  {canEditAttireContent ? (
                    <ActionButtons>
                      <TinyButton
                        type="button"
                        onClick={() => void handleMoveAttireLink(index, -1)}
                        disabled={index === 0 || attireBusyKey === `move:${link.id}`}
                      >
                        Up
                      </TinyButton>
                      <TinyButton
                        type="button"
                        onClick={() => void handleMoveAttireLink(index, 1)}
                        disabled={index === orderedAttireLinks.length - 1 || attireBusyKey === `move:${link.id}`}
                      >
                        Down
                      </TinyButton>
                      <TinyButton type="button" onClick={() => handleEditAttireLink(link)}>
                        Edit
                      </TinyButton>
                      <DangerButton
                        type="button"
                        onClick={() => void handleDeleteAttireLink(link)}
                        disabled={attireBusyKey === `delete:${link.id}`}
                      >
                        {attireBusyKey === `delete:${link.id}` ? 'Deleting...' : 'Delete'}
                      </DangerButton>
                    </ActionButtons>
                  ) : null}
                </ListItem>
              ))}
            </List>
          ) : null}
        </div>
      </Card>

      <Card>
        <SectionTitle>Delete Group</SectionTitle>
        <MutedText>
          This action uses safe deletion. Type <strong>{confirmationTarget}</strong> to confirm.
        </MutedText>
        <SearchRow style={{ marginTop: '0.65rem' }}>
          <SearchInput
            value={deleteConfirmationText}
            onChange={(event) => setDeleteConfirmationText(event.target.value)}
            placeholder="Type group name to confirm"
            autoComplete="off"
            autoCapitalize="off"
          />
          <DangerButton type="button" disabled={isDeletingGroup} onClick={handleDeleteGroup}>
            {isDeletingGroup ? 'Deleting...' : 'Delete Group'}
          </DangerButton>
        </SearchRow>
      </Card>

      {manageError ? <HelperText $tone="error">{manageError}</HelperText> : null}
      {manageSuccess ? <HelperText $tone="success">{manageSuccess}</HelperText> : null}
        </>
      ) : null}
    </Page>
  );
};

export default GroupManagePage;
